/**
 * Permissions utility for role-based access control
 *
 * Roles:
 * - 'admin': Can manage family, invite members, resend invitations, remove members
 * - 'member': Can view family members, create content, but cannot manage family
 * - 'viewer': Read-only access to family content
 */
import { getFamilies } from './secureStorage';

/**
 * Get the current user's role for a specific family
 * @param {string} familyId - The family ID to check role for
 * @returns {string|null} The user's role ('admin', 'member', 'viewer') or null if not found
 */
export const getUserRoleForFamily = (familyId) => {
  try {
    const families = getFamilies()
    if (!families || families.length === 0) return null

    const family = families.find(f => f.id === familyId)
    return family?.role || null
  } catch (error) {
    console.error('Error getting user role:', error)
    return null
  }
}

/**
 * Get the current user's role for the active family (stored in familyId)
 * Falls back to the first family's role if the stored familyId doesn't match
 * @returns {string|null} The user's role or null
 */
export const getCurrentUserRole = () => {
  try {
    const familyId = localStorage.getItem('familyId')
    const families = getFamilies()
    if (!families || families.length === 0) return null

    // Try to find the active family
    let family = families.find(f => f.id === familyId)

    // If not found, use the first family's role (but don't change familyId)
    if (!family && families.length > 0) {
      family = families[0]
    }

    return family?.role || null
  } catch (error) {
    console.error('Error getting user role:', error)
    return null
  }
}

/**
 * Check if the current user is an admin for the active family
 * @returns {boolean}
 */
export const isAdmin = () => {
  return getCurrentUserRole() === 'admin'
}

/**
 * Check if the current user is at least a member (admin or member)
 * @returns {boolean}
 */
export const isMemberOrAbove = () => {
  const role = getCurrentUserRole()
  return role === 'admin' || role === 'member'
}

/**
 * Check if user can invite family members
 * Both admins and members can invite family
 * @returns {boolean}
 */
export const canInviteMembers = () => {
  return isMemberOrAbove()
}

/**
 * Check if user can resend invitations
 * Only admins can resend
 * @returns {boolean}
 */
export const canResendInvitation = () => {
  return isAdmin()
}

/**
 * Check if user can remove family members
 * Only admins can remove
 * @returns {boolean}
 */
export const canRemoveMembers = () => {
  return isAdmin()
}

/**
 * Check if user can edit family settings
 * Only admins can edit settings
 * @returns {boolean}
 */
export const canEditFamilySettings = () => {
  return isAdmin()
}

/**
 * Check if user can view family members
 * All roles can view members
 * @returns {boolean}
 */
export const canViewMembers = () => {
  const role = getCurrentUserRole()
  return role === 'admin' || role === 'member' || role === 'viewer'
}

/**
 * Check if user can create content (stories, time capsules, etc.)
 * Admins and members can create
 * @returns {boolean}
 */
export const canCreateContent = () => {
  return isMemberOrAbove()
}

/**
 * Permission checks object for easy destructuring
 */
export const getPermissions = () => ({
  isAdmin: isAdmin(),
  isMemberOrAbove: isMemberOrAbove(),
  canInviteMembers: canInviteMembers(),
  canResendInvitation: canResendInvitation(),
  canRemoveMembers: canRemoveMembers(),
  canEditFamilySettings: canEditFamilySettings(),
  canViewMembers: canViewMembers(),
  canCreateContent: canCreateContent(),
  role: getCurrentUserRole()
})
